/** Trade-screen notices: the account restriction banner and the low-margin warning on the account. */
import { D } from '@platform/shared';
import { Link } from '../lib/router';
import { useMarginLevels, useTicker } from '../stores/market';
import { useAccountKind, useMe } from '../stores/session';
import { useAccountMarginLevel } from '../stores/trading';
import { cx, Icon } from './ui';

const pct = (x: D) => `${x.mul(100).toFixed(0)}%`;

/** Close-only or blocked trading (set by support), on the live account. */
export function TradingStatusBanner({ className }: { className?: string }) {
  const me = useMe();
  const kind = useAccountKind();
  if (!me || kind !== 'live' || me.tradingStatus === 'enabled') return null;
  const closeOnly = me.tradingStatus === 'close_only';
  return (
    <div role="status" className={cx('flex items-start gap-2.5 border-b px-4 py-2.5 text-[13px]', closeOnly ? 'border-warn/30 bg-warn-bg text-warn' : 'border-down/30 bg-down-bg text-down', className)}>
      <Icon name="lock" size={15} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <b className="font-semibold">{closeOnly ? 'Trading: close only' : 'Trading is blocked'}</b>
        <span className="text-fg/80">
          {' — '}
          {closeOnly ? 'you can close open positions but not open new ones.' : 'you cannot open or close positions right now. Contact support.'}
          {me.tradingStatusReason && <> Reason: {me.tradingStatusReason}.</>}
        </span>
      </div>
    </div>
  );
}

/** The instrument has no live price feed: its price is indicative and the live account can't trade it. */
export function PriceFeedBanner({ symbol, className }: { symbol: string; className?: string }) {
  const ticker = useTicker(symbol);
  const kind = useAccountKind();
  if (!ticker?.paused) return null;
  return (
    <div role="status" className={cx('flex items-start gap-2.5 border-b border-warn/30 bg-warn-bg px-4 py-2.5 text-[13px] text-warn', className)}>
      <Icon name="info" size={15} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <b className="font-semibold">Indicative price</b>
        <span className="text-fg/80">
          {' — '}
          {kind === 'live'
            ? 'the live price feed for this market is unavailable, so trading on your live account is paused until it returns.'
            : 'the live price feed for this market is unavailable. Your demo account can still trade on this price.'}
        </span>
      </div>
    </div>
  );
}

/**
 * Amber banner when the account margin level (equity ÷ used margin) is at or below the warning level.
 * Margin is cross: positions are only closed when the whole account reaches the stop-out level.
 */
export function MarginWarning({ className }: { className?: string }) {
  const levels = useMarginLevels();
  const kind = useAccountKind();
  const ml = useAccountMarginLevel();
  if (!levels || !ml || ml.gt(levels.warning)) return null;
  return (
    <div role="alert" className={cx('flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-warn-bg px-3 py-2 font-sans text-[12.5px] text-warn', className)}>
      <Icon name="info" size={15} className="shrink-0" />
      <span className="min-w-0 flex-1 whitespace-normal">
        Your account is at <b className="num">{pct(D.max(ml, D.ZERO))}</b> margin level. At <b className="num">{pct(D.from(levels.liquidation))}</b> open positions are closed
        automatically, starting with the largest loss.
      </span>
      {kind === 'live' && (
        <Link to="/account/deposit" className="flex h-[26px] shrink-0 items-center rounded-md bg-accent px-3 text-xs font-semibold text-white no-underline">
          Deposit
        </Link>
      )}
    </div>
  );
}
